import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, 
  Send, 
  Bot, 
  User,
  Heart,
  Brain,
  Smile,
  Activity,
  Moon,
  Sun,
  Coffee,
  Book,
  HelpCircle,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Clock,
  Calendar,
  Star,
  TrendingUp,
  Shield,
  Users,
  Sparkles,
  X,
  ArrowRight,
  CheckCircle,
  Info,
  Lightbulb,
  Target,
  Award,
  BookOpen,
  ExternalLink,
  Download,
  Share2,
  Bookmark,
  ThumbsUp,
  MessageSquare
} from 'lucide-react';
import api from '../../config/api';
import { v4 as uuidv4 } from 'uuid';

/**
 * MentalHealthGuide Component
 * 
 * Based on GitHub best practices and patterns from:
 * - Babylon Health React components
 * - Headspace web app architecture
 * - BetterHelp interface patterns
 * - Woebot conversational UI
 * - Material-UI responsive patterns
 * - ChatGPT streaming text implementation
 * 
 * Features:
 * - AI-powered chatbot interface with streaming text responses
 * - Smart question suggestions with full interactivity
 * - Expandable article system with detailed content views
 * - Tablet-optimized responsive grid layout (768px - 1024px)
 * - Human-like typing experience with variable speed
 * - Smooth animations and transitions
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Integration with App.js routing structure from Rhythm of life platform
 */

const MentalHealthGuide = () => {
  // State management
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showWelcome, setShowWelcome] = useState(true);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [bookmarkedArticles, setBookmarkedArticles] = useState([]);
  const [likedArticles, setLikedArticles] = useState([]);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [sessionMood, setSessionMood] = useState(null);
  const [typingSpeed, setTypingSpeed] = useState(30); // Faster typing speed
  const [uniqueId, setUniqueId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const streamingIntervalRef = useRef(null);

  // Optional scroll to bottom - only when explicitly needed
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  // Remove automatic scrolling - let user control scrolling

  // Initialize with welcome message with streaming effect
  useEffect(() => {
    // Generate or retrieve unique ID
    let storedId = sessionStorage.getItem('mentalHealthGuide_uniqueId');
    if (!storedId) {
      storedId = uuidv4();
      sessionStorage.setItem('mentalHealthGuide_uniqueId', storedId);
    }
    setUniqueId(storedId);

    // Ensure chat is in view
    const chatElement = document.getElementById('chat-interface');
    if (chatElement) {
      chatElement.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    
    const timer = setTimeout(() => {
      const welcomeText = "Hello! I'm your Mental Health Guide. I'm here to provide support, information, and resources for your mental well-being. How are you feeling today?";
      // Don't scroll on initial message
      const isFirstMessage = messages.length === 0;
      addStreamingMessage(welcomeText, 'bot', isFirstMessage);
      setShowWelcome(false);
    }, 1500); // Slightly longer initial delay for page load
    return () => clearTimeout(timer);
  }, []);

  // Simulate user typing indicator
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsUserTyping(true);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, 1000);
  };

  // Streaming text effect for bot messages - character by character for smoothness
  const addStreamingMessage = (fullText, type = 'bot', noScroll = false) => {
    const msgId = Date.now();
    
    // Add empty message first
    const newMessage = {
      id: msgId,
      type: type,
      text: '',
      timestamp: new Date(),
      avatar: type === 'bot' ? <Bot className="w-5 h-5" /> : <User className="w-5 h-5" />,
      isStreaming: true
    };
    
    setMessages(prev => [...prev, newMessage]);
    setStreamingMessageId(msgId);
    setIsTyping(true);
    
    // Character-by-character streaming for smoother effect
    let currentIndex = 0;
    const characters = fullText.split('');
    let currentText = '';
    
    const typeNextCharacter = () => {
      if (currentIndex < characters.length) {
        currentText += characters[currentIndex];
        setCurrentStreamingMessage(currentText);
        
        // Update the message in the messages array
        setMessages(prev => prev.map(msg => 
          msg.id === msgId 
            ? { ...msg, text: currentText }
            : msg
        ));
        
        // Auto-scroll during streaming (every few characters for smooth experience)
        if (currentIndex % 3 === 0) {
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }, 10);
        }
        
        currentIndex++;
        
        // Variable typing speed for more natural feel
        let nextDelay = typingSpeed;
        
        // Add pauses after punctuation for natural rhythm
        if (characters[currentIndex - 1] === '.' || characters[currentIndex - 1] === '!' || characters[currentIndex - 1] === '?') {
          nextDelay = typingSpeed * 4; // Longer pause after sentences
        } else if (characters[currentIndex - 1] === ',' || characters[currentIndex - 1] === ':' || characters[currentIndex - 1] === ';') {
          nextDelay = typingSpeed * 2; // Medium pause after commas
        } else if (characters[currentIndex - 1] === ' ') {
          // Slightly variable speed between words
          nextDelay = typingSpeed + Math.random() * 20;
        }
        
        // Schedule next character
        streamingIntervalRef.current = setTimeout(typeNextCharacter, nextDelay);
      } else {
        // Streaming complete
        setMessages(prev => prev.map(msg => 
          msg.id === msgId 
            ? { ...msg, isStreaming: false }
            : msg
        ));
        setIsTyping(false);
        setStreamingMessageId(null);
        setCurrentStreamingMessage('');
        
        // Final scroll to ensure message is fully visible
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 100);
        
        // Focus input box after bot response completes
        if (type === 'bot') {
          setTimeout(() => {
            inputRef.current?.focus();
          }, 200);
        }
        
        // Add quick actions after bot response
        if (type === 'bot' && !noScroll) {
          setTimeout(() => {
            setShowQuickActions(true);
          }, 300);
        }
      }
    };
    
    // Start typing after a brief delay
    setTimeout(() => {
      typeNextCharacter();
    }, 200);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamingIntervalRef.current) {
        clearTimeout(streamingIntervalRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Question suggestions based on mental health topics
  const questionSuggestions = [
    {
      icon: <Heart className="w-4 h-4" />,
      text: "I'm feeling anxious lately",
      category: 'anxiety'
    },
    {
      icon: <Moon className="w-4 h-4" />,
      text: "I'm having trouble sleeping",
      category: 'sleep'
    },
    {
      icon: <Brain className="w-4 h-4" />,
      text: "How can I manage stress better?",
      category: 'stress'
    },
    {
      icon: <Smile className="w-4 h-4" />,
      text: "I want to improve my mood",
      category: 'mood'
    },
    {
      icon: <Activity className="w-4 h-4" />,
      text: "Tips for staying motivated",
      category: 'motivation'
    },
    {
      icon: <Users className="w-4 h-4" />,
      text: "I'm struggling with relationships",
      category: 'relationships'
    },
    {
      icon: <Coffee className="w-4 h-4" />,
      text: "How to build healthy habits?",
      category: 'habits'
    },
    {
      icon: <Shield className="w-4 h-4" />,
      text: "Coping with depression",
      category: 'depression'
    }
  ];

  // Articles data with full content for expansion
  const articles = [
    {
      id: 1,
      title: "Understanding Anxiety: A Comprehensive Guide",
      category: "Anxiety",
      author: "Dr. Sarah Johnson",
      authorCredentials: "PhD in Clinical Psychology, 15 years experience",
      readTime: "8 min read",
      date: "2025-01-20",
      excerpt: "Learn about anxiety symptoms, triggers, and evidence-based coping strategies to manage anxiety effectively.",
      tags: ["Anxiety", "Coping", "Mental Health"],
      rating: 4.8,
      views: 15234,
      likes: 892,
      comments: 47,
      content: {
        introduction: "Anxiety is one of the most common mental health challenges, affecting millions worldwide. Understanding its nature is the first step toward managing it effectively.",
        sections: [
          {
            title: "What is Anxiety?",
            content: "Anxiety is your body's natural response to stress. It's a feeling of fear or apprehension about what's to come. While occasional anxiety is normal, persistent and overwhelming anxiety may indicate an anxiety disorder.",
            tips: ["Recognize that anxiety is common", "Understand it's treatable", "Know you're not alone"]
          },
          {
            title: "Common Symptoms",
            content: "Anxiety manifests differently in everyone, but common symptoms include rapid heartbeat, sweating, trembling, difficulty concentrating, sleep problems, and avoidance behaviors.",
            tips: ["Track your symptoms", "Note triggers", "Monitor patterns"]
          },
          {
            title: "Evidence-Based Strategies",
            content: "Research shows that cognitive-behavioral therapy (CBT), mindfulness, regular exercise, and proper sleep hygiene can significantly reduce anxiety symptoms.",
            tips: ["Try the 5-4-3-2-1 grounding technique", "Practice deep breathing", "Maintain regular exercise"]
          }
        ],
        conclusion: "Remember, seeking help is a sign of strength. With the right tools and support, anxiety is highly manageable.",
        resources: ["National Alliance on Mental Illness", "Anxiety and Depression Association", "Local therapy services"]
      }
    },
    {
      id: 2,
      title: "Sleep Hygiene: Your Path to Better Rest",
      category: "Sleep",
      author: "Dr. Michael Antonio",
      authorCredentials: "MD, Sleep Medicine Specialist",
      readTime: "6 min read",
      date: "2025-01-18",
      excerpt: "Discover proven techniques to improve your sleep quality and establish healthy bedtime routines.",
      tags: ["Sleep", "Wellness", "Habits"],
      rating: 4.9,
      views: 12456,
      likes: 734,
      comments: 29,
      content: {
        introduction: "Quality sleep is fundamental to mental health. Poor sleep can exacerbate anxiety, depression, and other mental health conditions.",
        sections: [
          {
            title: "The Sleep-Mental Health Connection",
            content: "Sleep and mental health are bidirectionally related. Poor sleep can trigger mental health issues, while mental health problems can lead to sleep disturbances.",
            tips: ["Aim for 7-9 hours nightly", "Keep consistent sleep schedule", "Create bedtime routine"]
          },
          {
            title: "Creating the Perfect Sleep Environment",
            content: "Your bedroom should be cool (60-67°F), dark, and quiet. Invest in comfortable bedding and consider blackout curtains or white noise machines.",
            tips: ["Remove electronic devices", "Use comfortable mattress", "Control room temperature"]
          },
          {
            title: "Pre-Sleep Routine",
            content: "Establish a wind-down routine 30-60 minutes before bed. This might include reading, gentle stretching, or meditation.",
            tips: ["Avoid screens 1 hour before bed", "Try chamomile tea", "Practice progressive muscle relaxation"]
          }
        ],
        conclusion: "Good sleep hygiene is an investment in your mental and physical health. Start with small changes and build consistency.",
        resources: ["Sleep Foundation", "CDC Sleep Guidelines", "Sleep diary templates"]
      }
    },
    {
      id: 3,
      title: "Mindfulness Meditation for Beginners",
      category: "Mindfulness",
      author: "Dr. Lissa Davis",
      authorCredentials: "MBSR Certified Instructor",
      readTime: "10 min read",
      date: "2025-01-15",
      excerpt: "Start your mindfulness journey with simple, practical meditation exercises you can do anywhere.",
      tags: ["Mindfulness", "Meditation", "Stress"],
      rating: 4.7,
      views: 18923,
      likes: 1203,
      comments: 68,
      content: {
        introduction: "Mindfulness meditation has been scientifically proven to reduce stress, anxiety, and depression while improving overall well-being.",
        sections: [
          {
            title: "What is Mindfulness?",
            content: "Mindfulness is the practice of being fully present and engaged in the moment, aware of your thoughts and feelings without judgment.",
            tips: ["Start with 5 minutes daily", "Be patient with yourself", "Practice regularly"]
          },
          {
            title: "Basic Breathing Meditation",
            content: "Sit comfortably, close your eyes, and focus on your breath. When your mind wanders (and it will), gently return attention to breathing.",
            tips: ["Count breaths from 1-10", "Focus on breath sensation", "Accept wandering thoughts"]
          },
          {
            title: "Body Scan Technique",
            content: "Systematically focus on different parts of your body, noticing sensations without trying to change anything. Start from toes and move upward.",
            tips: ["Practice lying down", "Take your time", "Notice without judging"]
          }
        ],
        conclusion: "Mindfulness is a skill that develops with practice. Be compassionate with yourself as you learn.",
        resources: ["Headspace app", "Insight Timer", "MBSR programs near you"]
      }
    },
    {
      id: 4,
      title: "Building Resilience in Challenging Times",
      category: "Resilience",
      author: "Dr. James Wilson",
      authorCredentials: "Resilience Research Center Director",
      readTime: "7 min read",
      date: "2025-01-12",
      excerpt: "Develop mental strength and bounce back from life's challenges with these resilience-building strategies.",
      tags: ["Resilience", "Coping", "Growth"],
      rating: 4.6,
      views: 9876,
      likes: 567,
      comments: 31,
      content: {
        introduction: "Resilience isn't about avoiding difficulties—it's about developing the skills to navigate and grow from challenges.",
        sections: [
          {
            title: "The Five Pillars of Resilience",
            content: "Research identifies five key factors: social connections, wellness practices, purpose, adaptability, and self-compassion.",
            tips: ["Nurture relationships", "Practice self-care", "Find meaning in struggles"]
          },
          {
            title: "Developing a Growth Mindset",
            content: "View challenges as opportunities for growth rather than threats. Focus on what you can learn from difficult experiences.",
            tips: ["Reframe negative thoughts", "Celebrate small wins", "Learn from setbacks"]
          },
          {
            title: "Building Your Support Network",
            content: "Strong relationships are crucial for resilience. Cultivate connections with family, friends, and community.",
            tips: ["Reach out regularly", "Be vulnerable", "Offer support to others"]
          }
        ],
        conclusion: "Resilience is like a muscle—it grows stronger with practice. Start building yours today.",
        resources: ["Resilience training programs", "Support groups", "Recommended books on resilience"]
      }
    },
    {
      id: 5,
      title: "Depression: Signs, Symptoms, and Support",
      category: "Depression",
      author: "Dr. Emily Roberts",
      authorCredentials: "Psychiatrist, Mood Disorders Specialist",
      readTime: "12 min read",
      date: "2025-01-10",
      excerpt: "Understanding depression and finding the right support system for your mental health journey.",
      tags: ["Depression", "Support", "Treatment"],
      rating: 4.9,
      views: 21345,
      likes: 1567,
      comments: 92,
      content: {
        introduction: "Depression is more than feeling sad—it's a medical condition that affects how you think, feel, and function in daily life.",
        sections: [
          {
            title: "Recognizing Depression",
            content: "Depression symptoms include persistent sadness, loss of interest, changes in appetite or sleep, fatigue, difficulty concentrating, and thoughts of worthlessness.",
            tips: ["Track mood changes", "Note physical symptoms", "Be honest with yourself"]
          },
          {
            title: "Treatment Options",
            content: "Effective treatments include therapy (especially CBT and IPT), medication, lifestyle changes, and in some cases, brain stimulation therapies.",
            tips: ["Consult professionals", "Be open to options", "Give treatments time"]
          },
          {
            title: "Self-Care Strategies",
            content: "While professional help is important, self-care practices like exercise, routine, social connection, and creative expression can support recovery.",
            tips: ["Start small", "Be consistent", "Celebrate progress"]
          }
        ],
        conclusion: "Recovery from depression is possible. With proper treatment and support, most people with depression feel better.",
        resources: ["National Suicide Prevention Lifeline", "Depression support groups", "Online therapy platforms"]
      }
    },
    {
      id: 6,
      title: "The Power of Positive Psychology",
      category: "Wellness",
      author: "Dr. Alex Martinez",
      authorCredentials: "Positive Psychology Researcher",
      readTime: "9 min read",
      date: "2025-01-08",
      excerpt: "Explore how positive psychology can transform your mindset and improve overall well-being.",
      tags: ["Positive Psychology", "Happiness", "Wellness"],
      rating: 4.5,
      views: 11234,
      likes: 823,
      comments: 44,
      content: {
        introduction: "Positive psychology focuses on what makes life worth living, studying strengths, virtues, and the factors that contribute to flourishing.",
        sections: [
          {
            title: "The Science of Happiness",
            content: "Research shows that happiness is influenced by genetics (50%), circumstances (10%), and intentional activities (40%)—meaning we have significant control.",
            tips: ["Practice gratitude daily", "Engage in acts of kindness", "Cultivate optimism"]
          },
          {
            title: "Character Strengths",
            content: "Identifying and using your character strengths leads to greater life satisfaction and achievement. Everyone has signature strengths to discover.",
            tips: ["Take strengths assessment", "Use strengths daily", "Appreciate others' strengths"]
          },
          {
            title: "Flow and Engagement",
            content: "Flow states—being fully absorbed in activities—contribute significantly to well-being. Seek activities that challenge and engage you.",
            tips: ["Find optimal challenge", "Minimize distractions", "Pursue meaningful goals"]
          }
        ],
        conclusion: "Positive psychology isn't about ignoring problems—it's about building resources to handle life's challenges and thrive.",
        resources: ["VIA Character Strengths Survey", "Authentic Happiness website", "Positive psychology courses"]
      }
    }
  ];

  // Categories for filtering articles
  const categories = ['all', 'Anxiety', 'Sleep', 'Mindfulness', 'Depression', 'Wellness', 'Resilience'];

  // Filter articles based on selected category
  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  // Function to detect depression-related content
  const isDepressionRelated = (message) => {
    const depressionKeywords = [
      'depress', 'depression', 'hopeless', 'hopelessness', 'worthless', 'worthlessness',
      'suicide', 'suicidal', 'kill myself', 'end my life', 'not worth living',
      'want to die', 'better off dead', 'no point', 'meaningless', 'empty',
      'numb', 'dead inside', 'can\'t go on', 'give up', 'tired of living',
      'self harm', 'hurt myself', 'cut myself', 'sad all the time', 'cry all day',
      'can\'t get out of bed', 'no energy', 'no motivation', 'feel nothing',
      'lonely', 'isolated', 'no one cares', 'burden', 'failure'
    ];
    
    const lowerMessage = message.toLowerCase();
    return depressionKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  // API call to chatbot
  const callChatbotAPI = async (message) => {
    try {
      // Ensure we have a unique ID
      if (!uniqueId) {
        console.error('No unique ID available');
        throw new Error('No unique ID available');
      }

      const payload = {
        message: message,
        is_depressed: isDepressionRelated(message),
        unique_id: uniqueId
      };

      const response = await api.post('/chatbot/chat', payload);
      return response.data;
    } catch (error) {
      console.error('Chatbot API error:', error);
      throw error;
    }
  };


  // Handle sending messages with streaming effect
  const handleSendMessage = (text = null) => {
    const messageText = text || inputValue;
    if (!messageText.trim()) return;

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: messageText,
      timestamp: new Date(),
      avatar: <User className="w-5 h-5" />
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowQuickActions(false);
    
    // Scroll to bottom after user message
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
    
    // Show typing indicator briefly before streaming
    setIsTyping(true);
    
    // Simulate more realistic processing time (1.5-2 seconds)
    const processingTime = 1500 + Math.random() * 500;
    
    setTimeout(async () => {
      try {
        const response = await callChatbotAPI(messageText);
        
        // Handle different possible response structures
        let botMessage = '';
        if (typeof response === 'string') {
          botMessage = response;
        } else if (response.reply) {
          botMessage = response.reply;
        } else if (response.message) {
          botMessage = response.message;
        } else if (response.response) {
          botMessage = response.response;
        } else if (response.text) {
          botMessage = response.text;
        } else if (response.data && response.data.reply) {
          botMessage = response.data.reply;
        } else if (response.data && response.data.message) {
          botMessage = response.data.message;
        } else if (response.data && response.data.response) {
          botMessage = response.data.response;
        } else {
          // If we can't find the message, use the entire response as string
          botMessage = JSON.stringify(response);
        }
        
        // Ensure we have a message to display
        if (!botMessage || botMessage.trim() === '') {
          botMessage = 'I understand. How can I help you further?';
        }
        
        addStreamingMessage(botMessage, 'bot');
        
        // Show quick actions after message is streamed
        const messageLength = botMessage.length;
        const estimatedTime = messageLength * typingSpeed + 1000;
        
        setTimeout(() => {
          setShowQuickActions(true);
        }, estimatedTime);
      } catch (error) {
        console.error('Error calling chatbot API:', error);
        // Show error message instead of fallback
        addStreamingMessage('I apologize, but I\'m having trouble connecting right now. Please try again in a moment.', 'bot');
        
        setTimeout(() => {
          setShowQuickActions(true);
        }, 2000);
      }
    }, processingTime);
  };

  // Handle question suggestion click
  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion.text);
  };

  // Handle article interactions
  const toggleBookmark = (articleId) => {
    setBookmarkedArticles(prev => {
      const updated = prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId];
      
      // Save to localStorage
      localStorage.setItem('bookmarkedArticles', JSON.stringify(updated));
      return updated;
    });
  };

  const toggleLike = (articleId) => {
    setLikedArticles(prev => {
      const updated = prev.includes(articleId)
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId];
      
      // Save to localStorage
      localStorage.setItem('likedArticles', JSON.stringify(updated));
      return updated;
    });
  };

  const handleArticleClick = (article) => {
    setExpandedArticle(article);
  };

  const closeExpandedArticle = () => {
    setExpandedArticle(null);
  };

  // Handle mood selection
  const handleMoodSelect = (mood) => {
    setSessionMood(mood);
    // Send mood to API instead of using local responses
    handleSendMessage(mood);
  };

  // Load saved preferences on mount
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    const savedBookmarks = localStorage.getItem('bookmarkedArticles');
    const savedLikes = localStorage.getItem('likedArticles');
    
    if (savedBookmarks) {
      setBookmarkedArticles(JSON.parse(savedBookmarks));
    }
    if (savedLikes) {
      setLikedArticles(JSON.parse(savedLikes));
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-serene-blue-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Brain className="w-8 h-8 text-serene-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Mental Health Guide</h1>
                <p className="text-sm text-gray-600">Your AI-powered mental wellness companion</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Shield className="w-4 h-4" />
              <span>Private & Confidential</span>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => {
                  const speeds = [20, 30, 40]; // Faster speed options
                  const currentIndex = speeds.indexOf(typingSpeed);
                  const nextIndex = (currentIndex + 1) % speeds.length;
                  setTypingSpeed(speeds[nextIndex]);
                }}
                className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                title="Click to adjust typing speed"
              >
                <Activity className="w-4 h-4" />
                <span>Speed: {typingSpeed === 20 ? 'Fast' : typingSpeed === 30 ? 'Normal' : 'Slow'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Optimized for Tablet and Desktop */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" style={{ paddingTop: '2rem' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat Interface - Main Column */}
          <div className="lg:col-span-2 space-y-6" id="chat-interface">
            <div className="bg-white rounded-xl shadow-lg overflow-visible">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-serene-blue-600 to-growth-green-600 px-6 py-4 sticky top-0 z-10">
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <MessageCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">Chat with Guide</h2>
                      <p className="text-sm opacity-90">Available 24/7</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm">Online</span>
                  </div>
                </div>
              </div>

              {/* Messages Container - Fixed height with better scrolling */}
              <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-gray-50" style={{ scrollBehavior: 'auto' }}>
                {showWelcome && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Sparkles className="w-12 h-12 text-serene-blue-500 mx-auto mb-3 animate-pulse" />
                      <p className="text-gray-500">Starting conversation...</p>
                    </div>
                  </div>
                )}
                
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
                  >
                    <div className={`flex items-start space-x-3 max-w-xs md:max-w-md ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      <div className={`p-2 rounded-lg ${message.type === 'user' ? 'bg-serene-blue-600 text-white' : 'bg-white text-gray-800'} ${message.isStreaming ? 'animate-pulse' : ''}`}>
                        {message.avatar}
                      </div>
                      <div>
                        <div className={`px-4 py-3 rounded-2xl ${message.type === 'user' ? 'bg-serene-blue-600 text-white' : 'bg-white shadow-md'}`}>
                          <p className="text-sm md:text-base whitespace-pre-wrap inline">
                            {message.text}
                            {message.isStreaming && (
                              <span className="typing-cursor" />
                            )}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 px-2">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isTyping && !streamingMessageId && (
                  <div className="flex justify-start animate-fadeIn">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 rounded-lg bg-white text-gray-800 animate-pulse">
                        <Bot className="w-5 h-5" />
                      </div>
                      <div className="flex items-center space-x-2 bg-white px-4 py-3 rounded-2xl shadow-md">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-serene-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-serene-blue-400 rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
                          <div className="w-2 h-2 bg-serene-blue-400 rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
                        </div>
                        <span className="text-xs text-gray-500 ml-2 italic">AI is composing a response...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Reference for scrolling - only when needed */}
                {messages.length > 0 && <div ref={messagesEndRef} style={{ height: '1px' }} />}
              </div>

              {/* Mood Quick Select (shows initially) */}
              {messages.length === 1 && !sessionMood && (
                <div className="border-t border-gray-200 p-4 bg-gradient-to-r from-serene-blue-50 to-growth-green-50">
                  <p className="text-sm text-gray-600 mb-3">How are you feeling today?</p>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {[
                      { mood: 'great', emoji: '😊', color: 'bg-green-100 hover:bg-green-200 text-green-700' },
                      { mood: 'good', emoji: '🙂', color: 'bg-blue-100 hover:bg-blue-200 text-blue-700' },
                      { mood: 'okay', emoji: '😐', color: 'bg-gray-100 hover:bg-gray-200 text-gray-700' },
                      { mood: 'anxious', emoji: '😰', color: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700' },
                      { mood: 'sad', emoji: '😢', color: 'bg-purple-100 hover:bg-purple-200 text-purple-700' },
                      { mood: 'stressed', emoji: '😣', color: 'bg-red-100 hover:bg-red-200 text-red-700' }
                    ].map((item) => (
                      <button
                        key={item.mood}
                        onClick={() => handleMoodSelect(item.mood)}
                        className={`p-3 rounded-lg transition-all duration-200 ${item.color} flex flex-col items-center space-y-1`}
                      >
                        <span className="text-2xl">{item.emoji}</span>
                        <span className="text-xs capitalize">{item.mood}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="border-t border-gray-200 p-4 bg-white">
                {isUserTyping && (
                  <div className="text-xs text-gray-500 mb-2 flex items-center">
                    <span className="inline-block w-1 h-1 bg-gray-400 rounded-full animate-pulse mr-1"></span>
                    You are typing...
                  </div>
                )}
                <div className="flex space-x-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-serene-blue-500 focus:border-transparent transition-all duration-200"
                    disabled={isTyping}
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isTyping}
                    className={`p-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-serene-blue-500 focus:ring-offset-2 ${
                      inputValue.trim() && !isTyping
                        ? 'bg-serene-blue-600 text-white hover:bg-serene-blue-700 transform hover:scale-105'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    aria-label="Send message"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Quick Actions */}
                {showQuickActions && messages.length > 2 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSendMessage("Tell me more about coping strategies")}
                      className="px-3 py-1 text-xs bg-serene-blue-100 text-serene-blue-700 rounded-full hover:bg-serene-blue-200 transition-colors"
                    >
                      Coping strategies
                    </button>
                    <button
                      onClick={() => handleSendMessage("I need professional help")}
                      className="px-3 py-1 text-xs bg-growth-green-100 text-growth-green-700 rounded-full hover:bg-growth-green-200 transition-colors"
                    >
                      Professional help
                    </button>
                    <button
                      onClick={() => handleSendMessage("Show me relaxation techniques")}
                      className="px-3 py-1 text-xs bg-calm-purple-100 text-calm-purple-700 rounded-full hover:bg-calm-purple-200 transition-colors"
                    >
                      Relaxation techniques
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Question Suggestions - Tablet Optimized Grid */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <HelpCircle className="w-5 h-5 mr-2 text-serene-blue-600" />
                Quick Questions
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[200px] overflow-y-auto pr-2">
                {questionSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="flex items-center space-x-2 p-3 bg-gray-50 hover:bg-serene-blue-50 rounded-lg transition-all duration-200 text-left group"
                  >
                    <div className="text-serene-blue-600 group-hover:scale-110 transition-transform">
                      {suggestion.icon}
                    </div>
                    <span className="text-sm text-gray-700 group-hover:text-serene-blue-700 line-clamp-2">
                      {suggestion.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Resources & Stats */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Wellness Journey</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-serene-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-serene-blue-600" />
                    <span className="text-sm text-gray-700">Days Active</span>
                  </div>
                  <span className="font-semibold text-serene-blue-700">7</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-growth-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="w-5 h-5 text-growth-green-600" />
                    <span className="text-sm text-gray-700">Progress</span>
                  </div>
                  <span className="font-semibold text-growth-green-700">85%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-calm-purple-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Star className="w-5 h-5 text-calm-purple-600" />
                    <span className="text-sm text-gray-700">Goals Met</span>
                  </div>
                  <span className="font-semibold text-calm-purple-700">12</span>
                </div>
              </div>
            </div>

            {/* Emergency Resources */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-2">Need Immediate Help?</h3>
                  <p className="text-sm text-red-700 mb-3">
                    If you're in crisis, please reach out:
                  </p>
                  <div className="space-y-2">
                    <a href="tel:+18884047470" className="flex items-center text-sm text-red-700 hover:text-red-800 font-medium">
                      <Phone className="w-4 h-4 mr-2" />
                      Crisis Helpline: +1 (888) 404-7470
                    </a>
                    <button className="text-sm text-red-700 hover:text-red-800 font-medium underline">
                      Find Emergency Resources →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Articles Section - Full Width */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6" id="articles-section">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Book className="w-6 h-6 mr-2 text-serene-blue-600" />
              Mental Health Articles
            </h2>
            
            {/* Category Filter - Tablet Optimized */}
            <div className="flex space-x-2 overflow-x-auto pb-2 max-w-full" style={{ scrollBehavior: 'auto' }}>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-serene-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid - Responsive for Tablet */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <article
                key={article.id}
                onClick={() => handleArticleClick(article)}
                className="bg-gray-50 rounded-lg p-5 hover:shadow-lg transition-all duration-200 cursor-pointer group transform hover:scale-[1.02]"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="inline-block px-3 py-1 bg-serene-blue-100 text-serene-blue-700 text-xs font-semibold rounded-full">
                    {article.category}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBookmark(article.id);
                      }}
                      className="text-gray-400 hover:text-yellow-500 transition-colors"
                    >
                      <Bookmark className={`w-4 h-4 ${bookmarkedArticles.includes(article.id) ? 'fill-current text-yellow-500' : ''}`} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLike(article.id);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <ThumbsUp className={`w-4 h-4 ${likedArticles.includes(article.id) ? 'fill-current text-red-500' : ''}`} />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-serene-blue-700 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                  {article.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center">
                      <User className="w-3 h-3 mr-1" />
                      {article.author}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {article.readTime}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {article.tags.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span className="flex items-center">
                        <Star className="w-3 h-3 mr-1 text-yellow-500 fill-current" />
                        {article.rating}
                      </span>
                      <span>·</span>
                      <span>{article.likes} likes</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Load More Button */}
          <div className="mt-6 text-center">
            <button 
              onClick={() => alert('Load more functionality would fetch additional articles')}
              className="px-6 py-2 bg-serene-blue-600 text-white rounded-lg hover:bg-serene-blue-700 transition-colors duration-200 font-medium"
            >
              Load More Articles
            </button>
          </div>
        </div>

        {/* Article Expanded View Modal */}
        {expandedArticle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn overflow-y-auto">
            <div className="bg-white rounded-xl max-w-4xl w-full my-8 shadow-2xl">
              <div className="max-h-[85vh] overflow-y-auto">
                {/* Article Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <span className="inline-block px-3 py-1 bg-serene-blue-100 text-serene-blue-700 text-xs font-semibold rounded-full mb-3">
                      {expandedArticle.category}
                    </span>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{expandedArticle.title}</h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {expandedArticle.author}
                      </span>
                      <span className="text-gray-400">·</span>
                      <span>{expandedArticle.authorCredentials}</span>
                      <span className="text-gray-400">·</span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {expandedArticle.readTime}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={closeExpandedArticle}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6">
                {/* Introduction */}
                <div className="mb-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {expandedArticle.content.introduction}
                  </p>
                </div>

                {/* Article Stats */}
                <div className="flex items-center space-x-6 p-4 bg-gray-50 rounded-lg mb-6">
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                    <span className="font-semibold">{expandedArticle.rating}</span>
                    <span className="text-gray-600">rating</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ThumbsUp className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold">{expandedArticle.likes}</span>
                    <span className="text-gray-600">likes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    <span className="font-semibold">{expandedArticle.comments}</span>
                    <span className="text-gray-600">comments</span>
                  </div>
                </div>

                {/* Sections */}
                {expandedArticle.content.sections.map((section, index) => (
                  <div key={index} className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                      <Lightbulb className="w-5 h-5 mr-2 text-serene-blue-600" />
                      {section.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {section.content}
                    </p>
                    {section.tips && (
                      <div className="bg-serene-blue-50 rounded-lg p-4">
                        <h4 className="font-semibold text-serene-blue-900 mb-2 flex items-center">
                          <Target className="w-4 h-4 mr-2" />
                          Quick Tips:
                        </h4>
                        <ul className="space-y-2">
                          {section.tips.map((tip, tipIndex) => (
                            <li key={tipIndex} className="flex items-start">
                              <CheckCircle className="w-4 h-4 text-serene-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}

                {/* Conclusion */}
                <div className="mb-6 p-4 bg-gradient-to-r from-serene-blue-50 to-growth-green-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Key Takeaway</h3>
                  <p className="text-gray-700">
                    {expandedArticle.content.conclusion}
                  </p>
                </div>

                {/* Resources */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-serene-blue-600" />
                    Additional Resources
                  </h3>
                  <ul className="space-y-2">
                    {expandedArticle.content.resources.map((resource, index) => (
                      <li key={index} className="flex items-center text-serene-blue-600 hover:text-serene-blue-700">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        <a href="#" className="underline">{resource}</a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => toggleBookmark(expandedArticle.id)}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                      bookmarkedArticles.includes(expandedArticle.id)
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Bookmark className="w-4 h-4" />
                    <span>{bookmarkedArticles.includes(expandedArticle.id) ? 'Bookmarked' : 'Bookmark'}</span>
                  </button>
                  <button
                    onClick={() => toggleLike(expandedArticle.id)}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                      likedArticles.includes(expandedArticle.id)
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <ThumbsUp className="w-4 h-4" />
                    <span>{likedArticles.includes(expandedArticle.id) ? 'Liked' : 'Like'}</span>
                  </button>
                  <button
                    onClick={() => alert('Share functionality would open sharing options')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-2 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                  <button
                    onClick={() => alert('Download functionality would save as PDF')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-2 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 p-3 bg-serene-blue-600 text-white rounded-full shadow-lg hover:bg-serene-blue-700 transition-all duration-200 z-50 group"
        aria-label="Scroll to top"
      >
        <ChevronRight className="w-6 h-6 rotate-[-90deg] group-hover:translate-y-[-2px] transition-transform" />
      </button>

      {/* Add custom styles for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes blink {
          0%, 49% {
            opacity: 1;
          }
          50%, 100% {
            opacity: 0;
          }
        }
        
        @keyframes cursorBlink {
          0%, 45% {
            opacity: 1;
          }
          50%, 95% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-blink {
          animation: blink 1s infinite;
        }
        
        .typing-cursor {
          display: inline-block;
          width: 2px;
          height: 1.2em;
          background-color: #718096;
          animation: cursorBlink 1s infinite;
          margin-left: 1px;
          vertical-align: text-bottom;
          opacity: 0.8;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Custom scrollbar for chat and other scrollable areas */
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>
    </div>
  );
};

// Add Phone icon since it's not in lucide-react by default
const Phone = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

export default MentalHealthGuide;